/**
 * useOwnershipCurrent / useOwnershipHistory — TanStack Query reads over
 * the Property ownership endpoints (SCRUM-172).
 *
 *   - `GET /organizations/:slug/properties/:propertyId/ownership/current`
 *     → the active ownership, or `null` (no current owner — empty-ready,
 *     NOT an error: a property with no owner yet is a valid state with
 *     an "Add owner" affordance).
 *   - `GET /organizations/:slug/properties/:propertyId/ownership/history`
 *     → `OwnershipResponseDto[]` (a bare array — the endpoint returns the
 *     ownerships directly, NOT wrapped in `{ items }`), projected +
 *     reverse-chron.
 *
 * Same closed status union as `use-property-detail.ts` (loading / ready
 * / forbidden / error). Both hooks accept an optional `resolveOwnerName`
 * (from `useOrgUsers`) and project in the hook body so names fill in
 * once the member list resolves, without re-fetching ownership.
 *
 * Note the path param is `{propertyId}` (not `{id}` as on the property
 * detail endpoint).
 */

import { useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { projectOwnershipDto, type OwnerNameResolver } from './ownership-mapper.js';
import type { OwnershipView } from './types.js';
import { propertyQueryKeys } from './queryKeys.js';

type OwnershipResponseDto = components['schemas']['OwnershipResponseDto'];

const FORBIDDEN = Symbol('ownership:forbidden');
const NO_CURRENT = Symbol('ownership:no-current');

export type UseOwnershipCurrentResult =
  | { readonly status: 'loading'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'ready'; readonly data: OwnershipView | null; readonly refetch: () => void }
  | { readonly status: 'forbidden'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'error'; readonly data?: undefined; readonly refetch: () => void };

export type UseOwnershipHistoryResult =
  | { readonly status: 'loading'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'ready'; readonly data: readonly OwnershipView[]; readonly refetch: () => void }
  | { readonly status: 'forbidden'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'error'; readonly data?: undefined; readonly refetch: () => void };

export function useOwnershipCurrent(
  propertyId: string | undefined,
  resolveOwnerName?: OwnerNameResolver,
): UseOwnershipCurrentResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...propertyQueryKeys.ownership(propertyId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && propertyId !== undefined && propertyId.length > 0,
    retry: false,
    queryFn: async () => {
      if (propertyId === undefined) throw new Error('useOwnershipCurrent called without propertyId');
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/{propertyId}/ownership/current',
        { params: { path: { slug: orgSlug as string, propertyId } } },
      );
      if (result.response.status === 403) return FORBIDDEN;
      // No current owner is a valid empty state, not an error.
      if (result.response.status === 404) return NO_CURRENT;
      if (!result.response.ok) {
        throw new Error(
          `GET ownership/current for ${propertyId} failed with status ${result.response.status}`,
        );
      }
      return result.data ?? NO_CURRENT;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || propertyId === undefined || propertyId.length === 0 || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) return { status: 'error', refetch };
  if (query.data === FORBIDDEN) return { status: 'forbidden', refetch };
  if (query.data === NO_CURRENT) return { status: 'ready', data: null, refetch };
  return {
    status: 'ready',
    data: projectOwnershipDto(query.data, resolveOwnerName),
    refetch,
  };
}

export function useOwnershipHistory(
  propertyId: string | undefined,
  resolveOwnerName?: OwnerNameResolver,
): UseOwnershipHistoryResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...propertyQueryKeys.ownershipHistory(propertyId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && propertyId !== undefined && propertyId.length > 0,
    retry: false,
    queryFn: async () => {
      if (propertyId === undefined) throw new Error('useOwnershipHistory called without propertyId');
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/{propertyId}/ownership/history',
        { params: { path: { slug: orgSlug as string, propertyId } } },
      );
      if (result.response.status === 403) return FORBIDDEN;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET ownership/history for ${propertyId} failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || propertyId === undefined || propertyId.length === 0 || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) return { status: 'error', refetch };
  if (query.data === FORBIDDEN) return { status: 'forbidden', refetch };
  const projected = query.data.map((dto: OwnershipResponseDto) =>
    projectOwnershipDto(dto, resolveOwnerName),
  );
  return { status: 'ready', data: projected, refetch };
}
